/* eslint-disable react-hooks/exhaustive-deps */
import { Pencil, Plus, RotateCw, Search } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import {
  Col,
  Container,
  Form,
  Row,
  Table,
  InputGroup,
  Card
} from 'react-bootstrap'
import { useDebounce } from 'use-debounce'
import api from '../../../config/AxiosInstance'
import * as Common from '../../../utils/Common'
import Loader from '../../atoms/Loader'
import TableAction from '../../atoms/Table/TableAction'
import TablePagination from '../../atoms/Table/TablePagination'
import ModalAddUser from '../../modal/user/ModalAddUser'
import ModalUpdateUser from '../../modal/user/ModalUpdateUser'

function MasterUserPage() {
  const [modalAddShow, setModalAddShow] = useState(false)
  const [modalUpdateShow, setModalUpdateShow] = useState(false)
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState([])
  const [editId, setEditId] = useState(0)
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const [loading, setLoading] = useState(false)

  const abortControllerRef = useRef(null)
  const isMounted = useRef(false)
  const [debouncedQuery] = useDebounce(query, 500)

  const headerTitleStyle = {
    color: '#343a40',
    fontSize: '24px',
    fontWeight: '700'
  }

  const cardFilterStyle = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
    padding: '1.5rem',
    marginBottom: '1.5rem'
  }

  const labelStyle = {
    fontSize: '0.75rem',
    fontWeight: '700',
    color: '#333333',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
    letterSpacing: '0.5px'
  }

  const FormControlStyle = {
    height: '45px',
    borderRadius: '10px',
    border: '1px solid #e9ecef',
    backgroundColor: '#ffffff',
    fontSize: '0.9rem'
  }

  const tableHeaderStyle = {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#333333',
    letterSpacing: '0.5px',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #e9ecef'
  }

  useEffect(() => {
    if (isMounted.current) {
      if (meta.current_page !== 1) {
        setMeta((prev) => ({ ...prev, current_page: 1 }))
      } else {
        loadUser()
      }
    } else {
      isMounted.current = true
    }
  }, [debouncedQuery])

  useEffect(() => {
    loadUser()
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [meta.current_page, meta.per_page])

  const loadUser = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setLoading(true)
    try {
      const res = await api.get('/users/list', {
        params: {
          username: debouncedQuery,
          per_page: meta.per_page,
          page: meta.current_page
        },
        signal
      })

      if (res.data.success) {
        const { metadata, data } = res.data
        setUsers(data)
        setMeta(metadata)
      }
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        setLoading(false)
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  function handleOnChangeMeta(e) {
    const perPageValue = parseInt(e.target.value, 10)
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: perPageValue,
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMeta((prevMeta) => ({ ...prevMeta, current_page: selectedPage }))
    window.scrollTo(0, 0)
  }

  function handleEdit(id) {
    setModalUpdateShow(true)
    setEditId(id)
  }

  const resetFilter = () => {
    setQuery('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  return (
    <>
      <Container className="mb-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h3 style={headerTitleStyle}>Master User</h3>
          </Col>
          <Col className="text-end">
            <button
              className="btn btn-primary-orange rounded px-3 me-2"
              onClick={() => setModalAddShow(true)}
              style={{ fontSize: '0.85rem' }}
            >
              <Plus size={16} className="me-2" />
              Add New
            </button>
            <button
              className="btn btn-light text-secondary fw-bold rounded px-3 border"
              onClick={resetFilter}
              style={{ fontSize: '0.85rem' }}
            >
              <RotateCw size={16} className="me-2" />
              Reset Filters
            </button>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="gy-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Search Username</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRight: 'none',
                      borderTopLeftRadius: '10px',
                      borderBottomLeftRadius: '10px'
                    }}
                  >
                    <Search
                      size={16}
                      className="text-muted"
                    />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search user by username..."
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                  ></Form.Control>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Card>

        <Row className="mt-4">
          <Col md={12}>
            <Table
              responsive
              hover
              className="shadow-sm bg-white"
              style={{
                fontSize: '0.9rem',
                borderRadius: '12px',
                overflow: 'hidden'
              }}
            >
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Name
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Username
                  </th>
                  <th
                    className="p-2 align-middle text-center"
                    style={tableHeaderStyle}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={3} className="p-5 text-center">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && users.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      className="p-5 text-center text-muted fw-bold"
                    >
                      No Data Available
                    </td>
                  </tr>
                )}
                {!loading &&
                  users.map((user) => {
                    return (
                      <tr
                        key={user.id}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td className="p-2 align-middle">{user.name || '-'}</td>
                        <td className="p-2 align-middle">{user.username}</td>
                        <td className="p-2 align-middle">
                          <TableAction direction="center">
                            <button
                              className="btn btn-light text-primary border rounded p-2"
                              onClick={() => handleEdit(user.id)}
                              style={{ width: '35px', height: '35px' }}
                            >
                              <Pencil
                                size={14}
                              />
                            </button>
                          </TableAction>
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="p-3 border-top-0">
                    <TablePagination
                      meta={meta}
                      handlePageChange={handlePageChange}
                      handleOnChangeMeta={handleOnChangeMeta}
                    />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Col>
        </Row>
      </Container>
      <ModalAddUser
        show={modalAddShow}
        onHide={() => setModalAddShow(false)}
        refreshData={() => loadUser()}
      ></ModalAddUser>
      <ModalUpdateUser
        show={modalUpdateShow}
        onHide={() => setModalUpdateShow(false)}
        id={editId}
        refreshData={() => loadUser()}
      ></ModalUpdateUser>
    </>
  )
}

export default MasterUserPage
